// ObjectConnectorHandlesController.
//
// When a single non-line object is selected, this draws a round handle at the
// middle of each of its four edges. Dragging a handle pulls a line out of that
// edge — a live preview follows the pointer and snaps to other objects' edges,
// and on release a real line is created whose START is anchored to this object
// (so it follows when the object later moves). It reuses the exact line/anchor
// machinery the line tool uses: `place/line` with `pendingStartAnchor` /
// `pendingEndAnchor`, and the shared `findSnap` / `resolveAnchor` helpers.
//
// Structure mirrors the line handle controllers (selection-driven lifecycle,
// FC17-style drag guard, resyncs on commit / live drag / zoom).

import Konva from 'konva';
import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import type { BoardState } from '../core/state/board-state.js';
import type { WhiteboardBus } from '../core/bus/whiteboard-bus.js';
import type { ObjectRegistry } from '../core/object-registry.js';
import { SNAP_SCREEN_PX, HANDLE_FILL } from '../constants/whiteboard-config.constants.js';
import { createRoundSelectionHandle, sizeRoundSelectionHandle } from '../core/konva/selection-handle.js';
import { wireHoverFill } from '../core/konva/hover-cursor.js';
import { findSnap, isAnchorTarget, anchorBoundsOf, rotatePointAround, type AnchorTarget, type AnchorBox } from './line-anchor.js';
import type { AnchorSide, LineAnchor } from '../core/interfaces/board-object.interface.js';

const CONNECTOR_HANDLE_PREFIX = 'connector-';
const CONNECTOR_PREVIEW_STROKE = '#b8c4d1';
const SNAP_INDICATOR_STROKE = '#2a5fd7';
/** Below this pointer travel (screen px) a press is a click, not a line-draw. */
const MIN_DRAW_SCREEN_PX = 6;
/**
 * How far outside the edge (screen px) the round handle's centre sits. The line
 * still starts on the edge (anchor t=0.5); only the grab dot floats outside — so
 * it stays reachable when an on-object overlay (e.g. the textbox text editor)
 * covers the object's rectangle. Anke, 2026-09-29: 12 → 22, together with the
 * dot growing to `LINE_HANDLE_SIZE_PX` — the dot now clears the resize anchor on
 * the same edge midpoint by a visible gap instead of touching it.
 */
export const CONNECTOR_HANDLE_OFFSET_PX = 22;

const SIDES: readonly AnchorSide[] = ['top', 'right', 'bottom', 'left'];

/** Outward unit normal of each edge, for pushing the handle off the object. */
const OUTWARD: Record<AnchorSide, { nx: number; ny: number }> = {
  top: { nx: 0, ny: -1 },
  bottom: { nx: 0, ny: 1 },
  left: { nx: -1, ny: 0 },
  right: { nx: 1, ny: 0 },
};

/** The connector works on the object's anchor box (carries rotation + pivot). */
type Bbox = AnchorBox;

interface Draft {
  readonly sourceId: string;
  readonly side: AnchorSide;
  /** Line start = the edge midpoint (where the line anchors), NOT the handle. */
  readonly startX: number;
  readonly startY: number;
  /** The handle's resting position (offset outside the edge), for no-op detection. */
  readonly handleStartX: number;
  readonly handleStartY: number;
  endX: number;
  endY: number;
  endAnchor: LineAnchor | undefined;
}

export interface ObjectConnectorHandlesDeps {
  overlayLayer(): Konva.Layer | undefined;
  selectionLayer(): Konva.Layer | undefined;
  konvaNodeFor(id: string): Konva.Node | undefined;
  getReadonly(): boolean;
  registry(): ObjectRegistry;
  /** The themeable hover fill (read live from a CSS variable). */
  hoverFill(): string;
}

export class ObjectConnectorHandlesController implements ReactiveController {
  readonly #bus: WhiteboardBus;
  readonly #state: BoardState;
  readonly #deps: ObjectConnectorHandlesDeps;
  readonly #disconnect$ = new Subject<void>();
  #handles: Konva.Circle[] = [];
  #selectedId: string | null = null;
  #dragging = false;
  #draft: Draft | null = null;
  #preview: Konva.Line | null = null;
  #snapIndicator: Konva.Circle | null = null;
  #stateTeardown: Array<() => void> = [];

  constructor(
    host: ReactiveControllerHost,
    bus: WhiteboardBus,
    state: BoardState,
    deps: ObjectConnectorHandlesDeps,
  ) {
    this.#bus = bus;
    this.#state = state;
    this.#deps = deps;
    host.addController(this);
  }

  hostConnected(): void {
    this.#bus.selection$
      .pipe(takeUntil(this.#disconnect$), takeUntil(this.#bus.destroy$))
      .subscribe({ next: (ids) => this.#onSelectionChanged(ids) });

    this.#bus.liveTransform$
      .pipe(takeUntil(this.#disconnect$), takeUntil(this.#bus.destroy$))
      .subscribe({ next: () => this.#refresh('live') });

    this.#stateTeardown = [
      this.#state.on('object-updated', ({ object }) => {
        if (object.id === this.#selectedId) this.#refresh('state');
      }),
      this.#state.on('viewport-changed', () => this.#refresh('state')),
    ];
  }

  hostDisconnected(): void {
    this.#selectedId = null;
    this.#destroyHandles();
    this.#clearPreview();
    for (const off of this.#stateTeardown.splice(0)) off();
    this.#disconnect$.next();
    this.#disconnect$.complete();
  }

  #layer(): Konva.Layer | undefined {
    return this.#deps.overlayLayer() ?? this.#deps.selectionLayer();
  }

  #onSelectionChanged(ids: ReadonlyArray<string>): void {
    // Never disturb an in-flight connector drag — committing an on-object text
    // editor (e.g. when the drag begins on a just-placed textbox) can churn the
    // selection, which must not destroy the handle under the pointer.
    if (this.#dragging) return;
    let id: string | null = null;
    if (ids.length === 1 && !this.#deps.getReadonly()) {
      const obj = this.#state.getObject(ids[0]!);
      // Lines are not anchor targets, so they get no connector handles.
      if (obj && isAnchorTarget(obj, this.#deps.registry())) id = ids[0]!;
    }
    if (id === this.#selectedId && this.#handles.length > 0) {
      this.#refresh('state');
      return;
    }
    this.#destroyHandles();
    this.#selectedId = id;
    this.#refresh('state');
  }

  /** The object's box in world space; `origin` overrides its top-left for the
   * live-drag case (node moved, state not committed).
   *
   * Stefan, 2026-08-31 — bugfix: the connector handles (the dots at each edge
   * midpoint, shown for ANY selected anchor-target — no line needs to be
   * docked) stayed frozen at the pre-resize edges for the whole live-resize
   * gesture, only jumping to the correct spot once the mouse was released.
   * Root cause: `liveTransform$` (fired every frame during BOTH a drag AND a
   * resize, see `fireLiveTransform`/`onTransformLive` in
   * `o2d-whiteboard.element.ts`) landed here at `#refresh('live')` →
   * `#bboxOf(id, 'live')`, which used to copy the LIVE x/y from the Konva
   * node but left width/height at the object's STORED (pre-resize) value —
   * correct for a pure drag (size never changes), wrong for a resize (x/y
   * often does NOT change — e.g. dragging the bottom-right handle — while
   * width/height is exactly what IS changing live). By the time this fires,
   * `onTransformLive` has already folded the Konva scale into the node's own
   * `width()`/`height()` (reset to scale 1) for this exact frame, so reading
   * them here gives the true live size, not a scaled/stale one. */
  #bboxOf(id: string, source: 'state' | 'live'): Bbox | null {
    const obj = this.#state.getObject(id);
    if (!obj || !isAnchorTarget(obj, this.#deps.registry())) return null;
    const registry = this.#deps.registry();
    if (source === 'live') {
      const node = this.#deps.konvaNodeFor(id);
      // Live drag/resize: the node has moved and/or resized but state is not
      // committed — recompute the anchor box from the object's LIVE
      // top-left AND live size.
      if (node) {
        return anchorBoundsOf(
          { ...obj, x: node.x(), y: node.y(), width: node.width(), height: node.height() } as AnchorTarget,
          registry,
        );
      }
    }
    return anchorBoundsOf(obj as AnchorTarget, registry);
  }

  /** Edge midpoint (`t=0.5`) turned onto the object's rotated edge. */
  #edgePoint(b: Bbox, side: AnchorSide): { x: number; y: number } {
    let p: { x: number; y: number };
    switch (side) {
      case 'top':    p = { x: b.x + b.width / 2, y: b.y }; break;
      case 'bottom': p = { x: b.x + b.width / 2, y: b.y + b.height }; break;
      case 'left':   p = { x: b.x,               y: b.y + b.height / 2 }; break;
      case 'right':  p = { x: b.x + b.width,      y: b.y + b.height / 2 }; break;
    }
    return rotatePointAround(p.x, p.y, b.pivotX ?? b.x, b.pivotY ?? b.y, b.rotation ?? 0);
  }

  /**
   * World position of a handle's dot for `side`, pushed outside the (rotated)
   * edge. The outward normal is rotated by the same angle so the dot floats off
   * the correct — rotated — edge.
   */
  #handlePoint(bbox: Bbox, side: AnchorSide, scale: number): { x: number; y: number } {
    const p = this.#edgePoint(bbox, side);
    const n = OUTWARD[side];
    const rn = rotatePointAround(n.nx, n.ny, 0, 0, bbox.rotation ?? 0);
    const off = CONNECTOR_HANDLE_OFFSET_PX / scale;
    return { x: p.x + rn.x * off, y: p.y + rn.y * off };
  }

  /**
   * Reconcile the handles with the current geometry. Crucially, when the four
   * handles already exist they are REPOSITIONED, not torn down and rebuilt: a
   * text-commit `object-updated` fires synchronously inside the very mousedown
   * that starts a connector drag, and destroying the handle there would leave
   * Konva's hit test with nothing to grab — the press would fall through to the
   * stage and start a marquee instead.
   */
  #refresh(source: 'state' | 'live'): void {
    if (this.#dragging) return;
    if (this.#selectedId === null) {
      this.#destroyHandles();
      return;
    }
    const bbox = this.#bboxOf(this.#selectedId, source);
    if (!bbox) {
      this.#destroyHandles();
      return;
    }
    const scale = this.#state.viewport.scale || 1;
    if (this.#handles.length === SIDES.length) this.#repositionHandles(bbox, scale);
    else this.#buildHandles(bbox, scale);
  }

  #repositionHandles(bbox: Bbox, scale: number): void {
    for (let i = 0; i < this.#handles.length; i++) {
      const handle = this.#handles[i]!;
      handle.position(this.#handlePoint(bbox, SIDES[i]!, scale));
      sizeRoundSelectionHandle(handle, scale);
    }
    this.#layer()?.batchDraw();
  }

  #buildHandles(bbox: Bbox, scale: number): void {
    this.#destroyHandles();
    const layer = this.#layer();
    if (!layer) return;
    for (const side of SIDES) {
      const p = this.#handlePoint(bbox, side, scale);
      const handle = createRoundSelectionHandle({
        x: p.x,
        y: p.y,
        scale,
        name: `${CONNECTOR_HANDLE_PREFIX}${side}`,
      });
      this.#wireDrag(handle, side);
      wireHoverFill(handle, () => this.#deps.hoverFill(), HANDLE_FILL, 'crosshair');
      layer.add(handle);
      this.#handles.push(handle);
    }
    layer.batchDraw();
  }

  #wireDrag(handle: Konva.Circle, side: AnchorSide): void {
    handle.on('click tap', (e: Konva.KonvaEventObject<MouseEvent>) => {
      e.cancelBubble = true;
    });

    handle.on('dragstart', () => {
      const id = this.#selectedId;
      if (id === null) return;
      const bbox = this.#bboxOf(id, 'state');
      if (!bbox) return;
      this.#dragging = true;
      // The line starts on the edge itself, not at the offset handle dot.
      const anchor = this.#edgePoint(bbox, side);
      const rest = handle.position();
      this.#draft = {
        sourceId: id,
        side,
        startX: anchor.x,
        startY: anchor.y,
        handleStartX: rest.x,
        handleStartY: rest.y,
        endX: anchor.x,
        endY: anchor.y,
        endAnchor: undefined,
      };
      this.#preview = new Konva.Line({
        stroke: CONNECTOR_PREVIEW_STROKE,
        strokeWidth: 1,
        strokeScaleEnabled: false,
        listening: false,
      });
      this.#layer()?.add(this.#preview);
    });

    handle.on('dragmove', () => {
      const draft = this.#draft;
      if (!draft || !this.#preview) return;
      const end = handle.position();
      const snap = this.#snap(end.x, end.y, draft.sourceId);
      draft.endX = snap?.x ?? end.x;
      draft.endY = snap?.y ?? end.y;
      draft.endAnchor = snap?.anchor;
      this.#preview.points([draft.startX, draft.startY, draft.endX, draft.endY]);
      if (snap) this.#showSnapIndicator(snap.x, snap.y);
      else this.#hideSnapIndicator();
      this.#layer()?.batchDraw();
    });

    handle.on('dragend', () => {
      const draft = this.#draft;
      this.#dragging = false;
      this.#draft = null;
      this.#clearPreview();
      if (draft) this.#commit(handle, draft);
      this.#refresh('state');
    });
  }

  /** Turn a completed drag into a real line, unless it was effectively a click.
   * "Moved" is measured from the handle's resting dot, not the line start, so
   * the outward offset never counts as a drag. */
  #commit(handle: Konva.Circle, draft: Draft): void {
    const scale = this.#state.viewport.scale || 1;
    const dx = handle.x() - draft.handleStartX;
    const dy = handle.y() - draft.handleStartY;
    if (Math.hypot(dx, dy) * scale < MIN_DRAW_SCREEN_PX) return;

    this.#bus.uiActions$.next({
      type: 'place/line',
      from: { x: draft.startX, y: draft.startY },
      to: { x: draft.endX, y: draft.endY },
      pendingStartAnchor: { objectId: draft.sourceId, side: draft.side, t: 0.5 },
      ...(draft.endAnchor ? { pendingEndAnchor: draft.endAnchor } : {}),
    });
  }

  #snap(worldX: number, worldY: number, excludeId: string) {
    const scale = this.#state.viewport.scale || 1;
    return findSnap(
      this.#state.allObjects(),
      this.#deps.registry(),
      worldX,
      worldY,
      SNAP_SCREEN_PX / scale,
      excludeId,
    );
  }

  #showSnapIndicator(x: number, y: number): void {
    const layer = this.#layer();
    if (!layer) return;
    if (!this.#snapIndicator) {
      this.#snapIndicator = new Konva.Circle({
        radius: 5,
        fill: '#ffffff',
        stroke: SNAP_INDICATOR_STROKE,
        strokeWidth: 2,
        strokeScaleEnabled: false,
        listening: false,
      });
      layer.add(this.#snapIndicator);
    }
    this.#snapIndicator.position({ x, y });
    this.#snapIndicator.visible(true);
  }

  #hideSnapIndicator(): void {
    this.#snapIndicator?.visible(false);
  }

  #clearPreview(): void {
    this.#preview?.destroy();
    this.#preview = null;
    this.#snapIndicator?.destroy();
    this.#snapIndicator = null;
    this.#layer()?.batchDraw();
  }

  #destroyHandles(): void {
    this.#dragging = false;
    this.#draft = null;
    if (this.#handles.length === 0) return;
    for (const h of this.#handles) h.destroy();
    this.#handles = [];
    this.#layer()?.batchDraw();
  }
}

export const CONNECTOR_HANDLE_NAME_PREFIX = CONNECTOR_HANDLE_PREFIX;
